import { Stack as ChakraStack, StackProps } from '@chakra-ui/react'
import * as React from 'react'

import { BgColorProvider, useBgColorCtx } from '~theme/BgColorProvider'
import { TColorKey } from '~theme/typings'
import { getSpacingValue, spaces, TSingleSpace, TSpace } from '~ui/styleGuide/spaces'
import { THEME_KEY_ATTRIBUTE } from '~ui/themeDesignTools/config/themeDesignTools.config'

import { Hr } from './Hr'

type TChakraProps = Pick<
  StackProps,
  | 'id'
  | 'wrap'
  | 'width'
  | 'align'
  | 'justify'
  | 'isInline'
  | 'direction'
  | 'minWidth'
  | 'maxWidth'
  | 'minHeight'
  | 'flexGrow'
  | 'alignItems'
  | 'justifyContent'
  | 'shouldWrapChildren'
>

type TProps = RequiredChildren &
  TChakraProps & {
    /**
     * background color of the stack
     */
    bgColor?: TColorKey
    /**
     * key of predefined spaces, defines the space between items in the Stack
     */
    spacing?: TSingleSpace
    /**
     * space inside the stack,
     * can be single space key,
     * object with `horizontal` and `vertical` attributes
     * or separate values for each side `top`, `right`, `bottom`, `left
     */
    padding?: TSpace
    /**
     * space inside the stack,
     * should be a valid CSS string
     * use only when you need a very specific value
     * and styled system keys can’t be used instead
     * overrides the `padding` prop
     */
    _padding?: StackProps['padding']
    /**
     * If `true`, each stack item will show a divider of color computed based on bgColor of the Stack and `divider` color from theme.
     * You can still define your own divider via `divider` prop.
     */
    dividerProps?: Omit<PropsOf<typeof Hr>, 'children'>
  }

/**
 * actual implementation
 * used inside a BgProviderWrapper bellow
 */
const StackComponent: React.FC<TProps> = ({
  children,
  padding = `none`,
  _padding,
  spacing = `medium`,
  bgColor,
  dividerProps,
  ...chakraProps
}) => {
  const { bgColorValue } = useBgColorCtx()

  const stackItemSpace = spaces[spacing]
  const paddingValue = _padding ?? getSpacingValue(padding)

  const hasBgColorSet = Boolean(bgColor)
  const themeDesignProps = { [THEME_KEY_ATTRIBUTE]: bgColor }

  return (
    <ChakraStack
      {...chakraProps}
      {...themeDesignProps}
      spacing={stackItemSpace}
      padding={paddingValue}
      backgroundColor={hasBgColorSet ? bgColorValue : undefined}
      divider={
        dividerProps ? (
          <Hr {...dividerProps} margin={{ vertical: spacing, horizontal: `none` }} />
        ) : undefined
      }
    >
      {children}
    </ChakraStack>
  )
}

/**
 * wrapper around Chakra Stack with reduced props & custom spacing (key of predefined spaces)
 */
export const Stack: React.FC<TProps> = (props) => {
  // this is just a BgColorProvider wrapper, the actual component is above
  return (
    <BgColorProvider bgColor={props.bgColor ?? null}>
      <StackComponent {...props} />
    </BgColorProvider>
  )
}
